import { URDFRobot } from "urdf-loader";
import { BaseAnimator } from "./BaseAnimator";
import { ArmJointRole, ArmMotionMapper, Model } from "../models/Model";
import { ModelLoader } from "../panels/common/ModelLoader";
import * as THREE from "three";
import { IKAnime, IKArm, ikSeg } from "./IKAnime";
import { Vec3 } from "../models/SyntheticMap";
import { normalizeAngleToPi } from "../utils/spatial";
import { vectorFromChildToParent } from "./visual-utils";


export enum ArmAnimatorStates {
  Idle = 'idle',
  Walking = 'walking',
}

export class ArmAnimator extends BaseAnimator implements IKArm {
  robot: URDFRobot;
  model: Model;
  mapper: ArmMotionMapper;
  state: ArmAnimatorStates = ArmAnimatorStates.Idle;

  constructor(public loader: ModelLoader) {
    super();
    this.robot = loader.robot!;
    this.model = loader.model;
    this.mapper = this.model.motionMapper as ArmMotionMapper;
  }

  setPosition(position: THREE.Vector3): void {
    const pos = position.clone();
    const { cfg: baseZCfg } = this.jointForRole(ArmJointRole.BaseZ);
    const { cfg: baseYCfg } = this.jointForRole(ArmJointRole.BaseY);
    const { cfg: elbowYCfg } = this.jointForRole(ArmJointRole.ElbowY);
    const { cfg: wristZCfg } = this.jointForRole(ArmJointRole.WristZ);
    const { cfg: businessEndCfg } = this.jointForRole(ArmJointRole.BusinessEnd);
    const reach = elbowYCfg.offset.z + wristZCfg.offset.z + businessEndCfg.offset.z;
    const leg1 = elbowYCfg.offset.z;
    const leg2 = wristZCfg.offset.z + businessEndCfg.offset.z;
    // console.log({ reach, leg1, leg2 });
    const relative = pos; // this.robot.worldToLocal(pos);
    const { x, y } = relative;
    const baseZ = Math.atan2(y, x);
    relative.applyAxisAngle(new THREE.Vector3(0, 0, 1), -baseZ);
    relative.sub(new THREE.Vector3(baseZCfg.offset.x, baseZCfg.offset.y, baseZCfg.offset.z));
    relative.sub(new THREE.Vector3(baseYCfg.offset.x, baseYCfg.offset.y, baseYCfg.offset.z));
    if (relative.length() > reach * 0.99) {
      relative.normalize();
      relative.multiplyScalar(reach * 0.99);
    }
    // console.log({ relative });
    const l = relative.length();
    if (Math.abs(x) < 0.001 && Math.abs(y) < 0.001) {
      this.setJointAngle(ArmJointRole.BaseZ, 0);
    } else {
      this.setJointAngle(ArmJointRole.BaseZ, baseZ);
    }
    const polarY = Math.asin(relative.z / relative.length());
    const { angleA, angleB, angleC } = this.calculateTriangleAngles(leg1, leg2, l);
    // console.log({ polarY, angleA, angleB, angleC });
    this.setJointAngle(ArmJointRole.ElbowY, Math.PI - angleC);
    this.setJointAngle(ArmJointRole.BaseY, Math.PI / 2 - polarY - angleB);
  }

  public getPosition(): THREE.Vector3 {
    if (this.mapper.joints[ArmJointRole.BusinessEnd] === undefined) {
      return new THREE.Vector3(0, 0, 0);
    }
    const { joint } = this.jointForRole(ArmJointRole.BusinessEnd);
    const vec = vectorFromChildToParent(this.robot, joint, new THREE.Vector3(0, 0, 0));
    return vec;
  }

  private setJointAngle(role: ArmJointRole, angle: number) {
    const { joint, cfg } = this.jointForRole(role);
    const currentAngle = joint.angle;
    const newAngle = normalizeAngleToPi(cfg.zeroAngle + angle * (cfg.invert ? -1 : 1));
    // console.log({ role, currentAngle, newAngle });
    joint.setJointValue(newAngle);
  }

  private jointForRole(role: ArmJointRole) {
    const cfg = this.mapper.joints[role];
    if (!cfg) throw new Error(`config for ${role} not found`);
    const joint = this.robot.joints[cfg.name];
    if (!joint) throw new Error(`joint for role ${role} not found`);
    return { joint, cfg };
  }

  public getAnimations(): string[] {
    return ['walk'];
  }

  public playAnimation(name: string): void {
    if (name === 'walk') {
      this.walk();
    } else {
      console.warn(`Unknown animation: ${name}`);
    }
  }

  targetPosition = new THREE.Vector3(0, 0, 0);
  trackingPosition = new THREE.Vector3(0, 0, 0);
  velocity = new THREE.Vector3(0, 0, 0);
  speed = 0.8;
  public moveArm(pos: Vec3) {
    // console.clear();
    this.trackingPosition = this.getPosition();
    this.targetPosition.copy(pos);
    const delta = this.targetPosition.clone().sub(this.trackingPosition);
    const duration = delta.length() / this.speed;
    this.velocity.copy(delta).divideScalar(duration);
    // console.log(`Moving arm to ${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)} over ${duration.toFixed(2)}s`);
    // console.log(`velocity: ${this.velocity.x.toFixed(2)}, ${this.velocity.y.toFixed(2)}, ${this.velocity.z.toFixed(2)}`);
  }

  public stop() {
  }

  public render(delta: number): void {
    if (this.velocity.length() > 0.001) {
      // console.log(`delta: ${delta.toFixed(2)}, arm at ${this.trackingPosition.x.toFixed(2)}, ${this.trackingPosition.y.toFixed(2)}, ${this.trackingPosition.z.toFixed(2)}`);
      const move = this.velocity.clone().multiplyScalar(delta);
      // console.log(`move: ${move.x.toFixed(4)}, ${move.y.toFixed(4)}, ${move.z.toFixed(4)}`);
      this.trackingPosition.add(move);
      this.setPosition(this.trackingPosition);
      // console.log(`trackingPosition: ${this.trackingPosition.x.toFixed(2)}, ${this.trackingPosition.y.toFixed(2)}, ${this.trackingPosition.z.toFixed(2)}`);
      const toTarget = this.targetPosition.clone().sub(this.trackingPosition);
      if (toTarget.dot(this.velocity) <= 0) {
        this.trackingPosition.copy(this.targetPosition);
        this.velocity.set(0, 0, 0);
      }
    }
  }

  public autoConfig() {
  }

  public neutralize() {
    this.velocity.set(0, 0, 0);
    this.trackingPosition.set(0, 0, 0);
    for (const cfg of Object.values(this.mapper.joints)) {
      this.setJointAngle(cfg.role, 0);
    }
  }

  private calculateTriangleAngles(leg1: number, leg2: number, l: number): { angleA: number; angleB: number; angleC: number } {
    const cosA = (leg2 ** 2 + l ** 2 - leg1 ** 2) / (2 * leg2 * l);
    const angleA = Math.acos(cosA);
    const cosB = (leg1 ** 2 + l ** 2 - leg2 ** 2) / (2 * leg1 * l);
    const angleB = Math.acos(cosB);
    const angleC = Math.PI - angleA - angleB;
    return {
      angleA,
      angleB,
      angleC,
    };
  }

}
